// client/src/widgets/EnrollMachine/EnrollMachine.tsx
// Bouton « Enrôler une machine » (TopBar) + modale d'enrôlement Iris.
//
// Flux (ADR-0002) : l'utilisateur authentifié nomme sa machine → mint d'un token
// d'enrôlement (POST /staxx/iris/enroll/mint, requireAuth) → la modale affiche
// (a) le lien 1-clic staxx://enroll?token=<t> et (b) le token brut (repli collage),
// tous deux copiables, + l'échéance. Le token est usage-unique, TTL 60 min.
// Le token n'est jamais loggué : il vit uniquement dans le state local de la modale.

import { useCallback, useEffect, useRef, useState } from 'react'
import { mintEnrollment, buildStaxxEnrollUrl, type MintResult, type MintError } from '@/shared/lib/enroll'

type Phase = 'idle' | 'loading' | 'done'

function formatExpiry(expiresAtIso: string): string {
  const expires = new Date(expiresAtIso)
  if (Number.isNaN(expires.getTime())) return ''
  const heure = expires.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  const minutes = Math.max(0, Math.round((expires.getTime() - Date.now()) / 60000))
  return `Expire à ${heure} (dans ~${minutes} min)`
}

export function EnrollMachine() {
  const [isOpen, setIsOpen] = useState(false)
  const [label, setLabel] = useState('')
  const [phase, setPhase] = useState<Phase>('idle')
  const [result, setResult] = useState<MintResult | null>(null)
  const [error, setError] = useState<MintError | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const reset = useCallback(() => {
    setLabel('')
    setPhase('idle')
    setResult(null)
    setError(null)
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
    reset()
  }, [reset])

  // Fermeture via Échap
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, close])

  // Focus le champ à l'ouverture
  useEffect(() => {
    if (isOpen && phase === 'idle') inputRef.current?.focus()
  }, [isOpen, phase])

  const submit = useCallback(async () => {
    const trimmed = label.trim()
    if (trimmed.length === 0) {
      setError({ kind: 'invalid-label', message: 'Donnez un nom à la machine.' })
      return
    }
    setPhase('loading')
    setError(null)
    const outcome = await mintEnrollment(trimmed)
    if (outcome.ok) {
      setResult(outcome.data)
      setPhase('done')
    } else {
      setError(outcome.error)
      setPhase('idle')
    }
  }, [label])

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="enroll-trigger"
        title="Enrôler une machine (Iris / staxx)"
        style={{
          background: 'none',
          border: '1px solid var(--color-border)',
          padding: '2px 8px',
          cursor: 'pointer',
          color: 'var(--color-text-secondary)',
          fontSize: 11,
          fontFamily: 'var(--font-sans)',
          fontWeight: 500,
          borderRadius: 'var(--radius-sm)',
          whiteSpace: 'nowrap',
        }}
      >
        Enrôler
      </button>

      {isOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Enrôler une machine"
          onClick={close}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9998,
            padding: 'var(--space-4)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(520px, 100%)',
              maxHeight: '90dvh',
              overflowY: 'auto',
              background: 'var(--color-bg-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-elevated)',
              padding: 'var(--space-6)',
              fontFamily: 'var(--font-sans)',
              color: 'var(--color-text-body)',
            }}
          >
            {/* En-tête */}
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
              <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                Enrôler une machine
              </h2>
              <button
                onClick={close}
                aria-label="Fermer"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: 20, lineHeight: 1, padding: 4 }}
              >
                ×
              </button>
            </div>

            {phase !== 'done' && (
              <>
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)', lineHeight: 1.5 }}>
                  Donnez un nom à la machine à enrôler. Vous obtiendrez un lien d'enrôlement
                  à ouvrir dans l'app Iris (ou un token à coller).
                </p>

                <label
                  htmlFor="enroll-label"
                  style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 'var(--space-1)' }}
                >
                  Nom de la machine
                </label>
                <input
                  id="enroll-label"
                  ref={inputRef}
                  value={label}
                  maxLength={64}
                  onChange={(e) => setLabel(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && phase === 'idle') void submit() }}
                  placeholder="MacBook Fabrice"
                  disabled={phase === 'loading'}
                  style={{
                    width: '100%',
                    padding: 'var(--space-2) var(--space-3)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-bg-base)',
                    color: 'var(--color-text-body)',
                    fontSize: 'var(--text-sm)',
                  }}
                />
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--space-1)' }}>
                  1 à 64 caractères : lettres, chiffres, espace, tiret, underscore.
                </p>

                {error && (
                  <div
                    role="alert"
                    style={{
                      marginTop: 'var(--space-3)',
                      padding: 'var(--space-2) var(--space-3)',
                      background: 'var(--color-error-bg)',
                      color: 'var(--color-error)',
                      border: '1px solid var(--color-error)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 'var(--text-xs)',
                      lineHeight: 1.5,
                    }}
                  >
                    {error.message}
                  </div>
                )}

                <button
                  onClick={() => void submit()}
                  disabled={phase === 'loading'}
                  style={{
                    marginTop: 'var(--space-4)',
                    width: '100%',
                    padding: 'var(--space-2) var(--space-4)',
                    background: 'var(--color-accent)',
                    color: 'var(--color-on-accent)',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 600,
                    cursor: phase === 'loading' ? 'wait' : 'pointer',
                    opacity: phase === 'loading' ? 0.7 : 1,
                  }}
                >
                  {phase === 'loading' ? 'Génération…' : 'Enrôler cette machine'}
                </button>
              </>
            )}

            {phase === 'done' && result && (
              <EnrollResult result={result} onReset={reset} onClose={close} />
            )}
          </div>
        </div>
      )}
    </>
  )
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false)
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch {
      setCopied(false)
    }
  }
  return (
    <button
      onClick={() => void onCopy()}
      style={{
        flexShrink: 0,
        padding: '2px 10px',
        background: copied ? 'var(--color-success-bg)' : 'var(--color-bg-muted)',
        color: copied ? 'var(--color-success)' : 'var(--color-text-secondary)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-sm)',
        fontSize: 'var(--text-xs)',
        fontWeight: 600,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
      title={`Copier ${label}`}
    >
      {copied ? 'Copié' : 'Copier'}
    </button>
  )
}

function EnrollResult({ result, onReset, onClose }: { result: MintResult; onReset: () => void; onClose: () => void }) {
  const staxxUrl = buildStaxxEnrollUrl(result.enrollmentToken)
  const expiry = formatExpiry(result.expiresAt)

  const fieldBox: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    padding: 'var(--space-2) var(--space-3)',
    background: 'var(--color-bg-muted)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
  }
  const monoText: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--text-xs)',
    color: 'var(--color-text-body)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }

  return (
    <>
      {/* Avertissement */}
      <div
        style={{
          padding: 'var(--space-2) var(--space-3)',
          background: 'var(--color-warning-bg)',
          color: 'var(--color-warning)',
          border: '1px solid var(--color-warning)',
          borderRadius: 'var(--radius-sm)',
          fontSize: 'var(--text-xs)',
          lineHeight: 1.5,
          marginBottom: 'var(--space-4)',
        }}
      >
        Token à usage unique — il expire en ~60 min et ne sera affiché qu'une seule fois.
        {expiry && <> {expiry}.</>}
      </div>

      {/* Lien 1-clic */}
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 'var(--space-1)' }}>
          Lien d'enrôlement (1 clic — ouvre l'app Iris)
        </div>
        <div style={fieldBox}>
          <a href={staxxUrl} style={{ ...monoText, color: 'var(--color-accent)' }} title={staxxUrl}>
            {staxxUrl}
          </a>
          <CopyButton value={staxxUrl} label="le lien" />
        </div>
      </div>

      {/* Token brut — repli collage */}
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 'var(--space-1)' }}>
          Token (repli — à coller dans l'app si le lien ne fonctionne pas)
        </div>
        <div style={fieldBox}>
          <span style={monoText} title={result.enrollmentToken}>{result.enrollmentToken}</span>
          <CopyButton value={result.enrollmentToken} label="le token" />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
        <button
          onClick={onReset}
          style={{
            padding: 'var(--space-2) var(--space-4)',
            background: 'none',
            color: 'var(--color-text-secondary)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--text-sm)',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Enrôler une autre
        </button>
        <button
          onClick={onClose}
          style={{
            padding: 'var(--space-2) var(--space-4)',
            background: 'var(--color-accent)',
            color: 'var(--color-on-accent)',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--text-sm)',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Terminé
        </button>
      </div>
    </>
  )
}
